import { inject, Injectable, OnDestroy } from '@angular/core';
import axios, { AxiosPromise } from 'axios';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { ApplicationStateService } from './application-state.service';
import { QuickLinks } from '../models/quick-links';
import { environment } from '../../environments/environment';
import { QuickLinkFactory } from '../models/factories/quick-link-factory';
import { QuickLinksMap } from '../models/quick-links-map';
import { QuickLink, QuickLinkType } from '../models/quick-link';
import { TechnicalFeature } from '@lemberger/lemberger-am';
import { QuickLinkSignFormData } from '../models/quick-link-sign-form-data';
import { QuickLinkUtils } from '../utils/quick-link-utils';
import { CommunicationService } from './communication.service';
import { Subject, Subscription } from 'rxjs';
import { Position } from '../models/position';
import { PageService } from './page.service';
import _ from 'lodash';
import { QuickLinkSearchResult } from '../models/quick-link-search-result';
import { UserDataService } from './user-data.service';
import { DigiTrainerContent } from '../models/digi-trainer-content';
import { Link } from '../models/link';
import { ModalService } from './modal.service';
import { LinkService } from './link.service';
import { ResponseStatus } from '../models/modalable';
import { ModalableYedo } from '../models/modalable-yedo';
import { HelpService } from './help.service';
import { BookFeatureActivationService } from './book-feature-activation.service';
import { LinkFactory } from '../models/factories/link-factory';
import {
    DigiTrainerModalComponent
} from '../components/bookviewer/pages/modal-container/digi-trainer-modal/digi-trainer-modal.component';
import { TasksComponent } from '../components/sidebar/tasks/tasks.component';
import { SidebarService } from './sidebar.service';
import { Assignment } from '../models/assignment';
import { CrudService } from './crud.service';
import { AccessService } from './access.service';
import { ModalableEdusuite } from '../models/modalable-edusuite';
import { ModalableDigbi } from '../models/modalable-digbi';

@Injectable({
    providedIn: 'root'
})
export class QuickLinkService implements OnDestroy {
    private accessService = inject(AccessService);

    quickLinks: QuickLinksMap;
    private digiTrainerContent = new Subject<DigiTrainerContent | null>();
    digiTrainerContent$ = this.digiTrainerContent.asObservable();

    private cancelTokens: Array<any> = [];
    private subscription: Subscription;
    private cancelToken: any;
    private cancelHSMToken: any;
    private digiTrainerIndividualContent: any = null;
    private _fetchedAssignments: Array<Assignment> = [];

    constructor(private routeService: RouteService,
                private restService: RestService,
                private communicationService: CommunicationService,
                private applicationStateService: ApplicationStateService,
                private userDataService: UserDataService,
                private pageService: PageService,
                private modalService: ModalService,
                private linkService: LinkService,
                private sidebarService: SidebarService,
                private helpService: HelpService,
                private featureActivationService: BookFeatureActivationService,
                private linkFactory: LinkFactory,
                private crudService: CrudService) {
        this.subscription = this.communicationService.pageTurnConfirmed$.subscribe(
            () => {
                this.cancelRequests();

                if ((this.sidebarService.activeComponent === TasksComponent || this.applicationStateService.currentViewMode.showTopbar)) {
                    this.fetchDigiTrainerContent();
                }
            });
    }

    /**
     * get testlink for given link code
     *
     * @param {string} linkCode
     * @return {string}
     */
    static getTestLink(linkCode: string): string {
        return `${environment.endpointUrls.assignmentLink}/${linkCode}/assignment.json`;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * get testlink for given link code
     *
     * @param {QuickLink} quicklink
     * @return {string}
     */
    getHAMLink(quicklink: QuickLink): string {
        let linkCode = QuickLinkUtils.getLinkCode(quicklink.link);
        linkCode = this.convertQuickLink(linkCode, !quicklink.link.startsWith('https')).toLowerCase();
        if (linkCode.includes('am') || linkCode.includes('hsm')) {
            const regex = new RegExp('(am|hsm)', 'ig');

            linkCode = linkCode.replace(regex, '/$1/');

            return `${environment.endpointUrls.assignmentLink}/${linkCode}/assignment.json`;
        }

        const prefix = this.applicationStateService.bookConfig.quickLinkPrefix;
        const regex = new RegExp(`(${prefix})`, 'ig');

        linkCode = linkCode.replace(regex, '$1/');

        return `${environment.endpointUrls.assignmentLink}/${linkCode}/assignment.json`;

    }

    /**
     * Fetch single assignment data.
     * @param quickLink
     */
    async fetchAssignmentData(quickLink: string): Promise<Assignment | null> {
        const assignment = this._fetchedAssignments.find((assignment) => {
            return assignment?.meta?.quicklink?.toUpperCase() === quickLink.toUpperCase();
        });
        if (assignment) {
            return assignment;
        }

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklink, {
            quicklink: quickLink
        });

        try {
            const result = await this.restService.get(route);
            if (result) {
                this._fetchedAssignments.push(result);
            }

            if (this._fetchedAssignments.length > 6) {
                this._fetchedAssignments = this._fetchedAssignments.splice(0, 2);
            }

            return result;
        } catch {
            return null;
        }
    }

    fetchQuickLinks(): Promise<QuickLinksMap> {
        if (this.quickLinks) {
            return Promise.resolve(this.quickLinks);
        }

        return new Promise<QuickLinksMap>(resolve => {
            const route = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinks, {
                bookId: this.applicationStateService.bookId
            });

            this.restService.get(route)
                .then(result => {
                    if (this.pageService.hasLimitedView()) {
                        for (const key in result) {
                            if (result.hasOwnProperty(key) && Array.isArray(result[key])) {
                                result[key] = result[key].filter(
                                    (obj: any) => this.pageService.getGetViewablePagesIDs().includes(obj.pageId)
                                );
                            }
                        }
                    }

                    this.quickLinks = new QuickLinksMap();
                    const quicklinkFactory = new QuickLinkFactory(this.applicationStateService);
                    this.handleResult(quicklinkFactory, result.generic, null);
                    this.handleResult(quicklinkFactory, result.eSquirrel, QuickLinkType.E_SQUIRREL);
                    this.handleResult(quicklinkFactory, result.gmt, QuickLinkType.GMT);
                    this.handleResult(quicklinkFactory, result.hsm, QuickLinkType.HSM);
                    this.handleResult(quicklinkFactory, result.digiFit, QuickLinkType.DIGI_FIT);

                    resolve(this.quickLinks);
                })
                .catch(() => {
                    resolve(new QuickLinksMap());
                });
        });
    }

    /**
     * Fetches single quicklink assignment.json to use in DigiTrainer
     */
    async fetchDigiTrainerContent(quicklink?: string): Promise<void> {
        if (!this.applicationStateService.bookConfig.exerciseSeries) {
            return;
        }

        if (quicklink) {
            const content = await this.getDigiTrainerContentFromQuicklink(quicklink);
            this.digiTrainerContent.next(content);

            return;
        }

        if (!this.applicationStateService.bookConfig.quickLinkPrefix?.toUpperCase()) {
            return;
        }

        const currentPages = this.pageService.getCurrentPages();
        let contentCombined: DigiTrainerContent | null = null;

        for (const currentPage of currentPages) {
            // eslint-disable-next-line max-len
            const realPageNumber = this.pageService.getRealPageNumber(currentPage.index);

            if (realPageNumber < 1) {
                continue;
            }

            const quicklink = `${this.applicationStateService.bookConfig.quickLinkPrefix?.toUpperCase()}am${realPageNumber}`;

            const content = await this.getDigiTrainerContentFromQuicklink(quicklink);
            if (!content) {
                continue;
            }

            if (contentCombined === null) {
                contentCombined = content;
            } else {
                contentCombined.merge(content);
            }
        }

        this.digiTrainerContent.next(contentCombined);
    }

    /**
     * Gets all QuickLinks of a page.
     * @param {number} pageId
     * @returns {QuickLinks}
     */
    getAllQuickLinksOfPage(pageId: number): QuickLinks {

        const quickLinks = new QuickLinks();
        if (this.quickLinks.genericQuickLinks.has(pageId)) {
            quickLinks.genericQuickLinks = this.quickLinks.genericQuickLinks.get(pageId);
        } else {
            quickLinks.genericQuickLinks = [];
        }

        if (this.quickLinks.eSquirrelQuickLinks.has(pageId)) {
            quickLinks.eSquirrelQuickLinks = this.quickLinks.eSquirrelQuickLinks.get(pageId);
        } else {
            quickLinks.eSquirrelQuickLinks = [];
        }

        if (this.quickLinks.gmtQuickLinks.has(pageId)) {
            quickLinks.gmtQuickLinks = this.quickLinks.gmtQuickLinks.get(pageId);
        } else {
            quickLinks.gmtQuickLinks = [];
        }

        if (this.quickLinks.digiFitQuicklinks.has(pageId)) {
            quickLinks.digiFitQuicklinks = this.quickLinks.digiFitQuicklinks.get(pageId);
        } else {
            quickLinks.digiFitQuicklinks = [];
        }

        if (this.quickLinks.hsmQuickLinks.has(pageId)) {
            quickLinks.hsmQuickLinks = this.quickLinks.hsmQuickLinks.get(pageId);
            quickLinks.hsmQuickLinks.forEach(quickLink => {
                quickLink.type = QuickLinkType.HSM;
            });
        } else {
            quickLinks.hsmQuickLinks = [];
        }

        return quickLinks;
    }

    /**
     * Get first quicklink of page and given type.
     *
     * @param {number} pageId
     * @param {QuickLinkType} type
     */
    getFirstQuickLinkOfPageAndType(pageId: number, type: QuickLinkType): QuickLink | null {
        const quicklinks = this.getAllQuickLinksOfPage(pageId).all();

        return _.first(_.filter(quicklinks, (quicklink) => {
            return quicklink.type === type;
        }));
    }

    /**
     * Get first QuickLink of a page.
     * @param {number} pageId
     * @returns {QuickLink | null}
     */
    getFirsQuickLinkOfPage(pageId: number): QuickLink | null {
        const quicklinks = this.getAllQuickLinksOfPage(pageId).all();
        if (quicklinks.length === 0) {
            return null;
        }

        return quicklinks[0];
    }

    getAllActiveQuickLinksOfPage(pageId: number): QuickLinks {
        const quicklinks = this.getAllQuickLinksOfPage(pageId);
        const allDeactivatedQuicklinks = this.applicationStateService.globalVars.disabledAssignments;
        quicklinks.hsmQuickLinks = _.filter(quicklinks.hsmQuickLinks, quickLink => {
            return !_.includes(allDeactivatedQuicklinks, quickLink.slug);
        });

        quicklinks.genericQuickLinks = _.filter(quicklinks.genericQuickLinks, quickLink => {
            return !_.includes(allDeactivatedQuicklinks, quickLink.slug);
        });

        quicklinks.eSquirrelQuickLinks = _.filter(quicklinks.eSquirrelQuickLinks, quickLink => {
            return !_.includes(allDeactivatedQuicklinks, quickLink.slug);
        });

        quicklinks.gmtQuickLinks = _.filter(quicklinks.gmtQuickLinks, quickLink => {
            return !_.includes(allDeactivatedQuicklinks, quickLink.slug);
        });

        return quicklinks;
    }

    getCollectionQuickLinks(quickLink: QuickLink): Promise<Array<QuickLink>> {

        try {
            const url = this.routeService.getRelativeRoute(environment.endpointUrls.quicklink,
                { quicklink: QuickLinkUtils.getLinkCode(quickLink.link) });
            const collectionQuickLinks = [];

            return new Promise<Array<QuickLink>>(((resolve, reject) => {

                this.restService.get(url).then(result => {

                    if (result.assignments) {
                        const allGenericQuicklinks = _.flatten(Array.from(this.quickLinks.genericQuickLinks.values()));
                        result.assignments.forEach(quickLinkSlug => {
                            const foundQuickLink = _.find(allGenericQuicklinks, ele => {
                                return QuickLinkUtils.getLinkCode(ele.link) === quickLinkSlug;
                            });
                            if (foundQuickLink) {
                                collectionQuickLinks.push(foundQuickLink);
                            } else {
                                const quickLink = new QuickLink();
                                const platformDomain = this.applicationStateService.platformDomain;
                                quickLink.link = `${platformDomain}/${quickLinkSlug}`;
                                collectionQuickLinks.push(quickLink);
                            }
                        });

                    }
                    resolve(collectionQuickLinks);

                }).catch(err => {
                    reject(err);
                });
            }));
        } catch (e) {
            /* eslint-disable */
            console.error(e);

            return new Promise<Array<QuickLink>>((resolve, reject) => reject(e));
        }
    }

    getAllQuickLinksOfPageIndex(pageIndex: number): QuickLinks {
        return this.getAllQuickLinksOfPage(this.pageService.getPageIdByIndex(pageIndex));
    }

    pageHasQuickLink(pageId: number, quickLink: QuickLink): boolean {
        const quickLinks = this.getAllQuickLinksOfPage(pageId);

        for (const link of quickLinks.genericQuickLinks) {
            if (link.link === quickLink.link) {
                return true;
            }
        }

        for (const link of quickLinks.eSquirrelQuickLinks) {
            if (link.link === quickLink.link) {
                return true;
            }
        }

        for (const link of quickLinks.gmtQuickLinks) {
            if (link.link === quickLink.link) {
                return true;
            }
        }

        for (const link of quickLinks.hsmQuickLinks) {
            if (link.link === quickLink.link) {
                return true;
            }
        }

        return false;
    }

    /**
     * Gets QuickLink data which is signed.
     * @param {QuickLink} quickLink
     */
    getSignedQuickLinkInformation(quickLink: QuickLink): Promise<QuickLinkSignFormData> {

        let route;
        const prefix = this.applicationStateService.bookConfig.quickLinkPrefix;
        const linkCode = QuickLinkUtils.getLinkCode(quickLink.link);

        if (QuickLinkUtils.getMatchingQuickLinkType(linkCode, prefix) === QuickLinkType.E_SQUIRREL) {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignESQUIRREL,
                {
                    bookId: this.applicationStateService.bookId
                });
        } else if (QuickLinkUtils.getMatchingQuickLinkType(linkCode, prefix) === QuickLinkType.GMT) {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignGMT,
                {
                    bookId: this.applicationStateService.bookId
                });
        } else if (quickLink.isCalcuflow()) {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignCalcuflow,
                {
                    bookId: this.applicationStateService.bookId,
                });
        } else if (QuickLinkUtils.getMatchingQuickLinkType(linkCode, prefix) === QuickLinkType.PHASE) {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignPhase,
                {
                    bookId: this.applicationStateService.bookId
                });
        } else if (this.applicationStateService.bookConfig.bookSource === 'sia') {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.siaSign,
                {
                    bookId: this.applicationStateService.bookId
                });
        } else if (this.isSignableLtiConnection(quickLink)) {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.ltiLaunch,
                {
                    bookId: this.applicationStateService.bookId
                });
        } else {
            route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignEduvidual,
                {
                    bookId: this.applicationStateService.bookId,
                    resourceLinkId: linkCode
                });
        }

        const parameters = {
            quick_link: linkCode,
            custom_worksheet_id: undefined,
            custom_assignments: undefined,
            custom_worksheet_updated_at: undefined,
            target: quickLink.link,
            custom_worksheet_title: undefined,
        }

        if (quickLink.custom_worksheet_id) {
            parameters.custom_worksheet_id = quickLink.custom_worksheet_id;
        }
        if (quickLink.custom_assignments) {
            parameters.custom_assignments = quickLink.custom_assignments;
        }
        if (quickLink.custom_worksheet_updated_at) {
            parameters.custom_worksheet_updated_at = quickLink.custom_worksheet_updated_at;
        }
        if (quickLink.custom_worksheet_title) {
            parameters.custom_worksheet_title = quickLink.custom_worksheet_title;
        }
        return this.restService.post(route, parameters);

    }

    /**
     * Test if a QuickLink is valid and available
     * @param {QuickLink[]} quickLinks
     * @returns {Promise<QuickLink[]>}
     */
    testQuickLinks(quickLinks: Array<QuickLink>): Promise<Array<QuickLink>> {

        return new Promise(resolve => {

            const urlsToTest = [];
            const urlToQuickLinkMap = new Map<string, QuickLink>();
            quickLinks.forEach(quickLink => {
                if (!quickLink.tmpRedirect) {
                    urlsToTest.push(this.getHAMLink(quickLink));
                    urlToQuickLinkMap.set(this.getHAMLink(quickLink), quickLink);
                } else {
                    quickLink.found = 1;
                }
            });

            const testRoute = this.routeService.getAbsoluteRoute(environment.endpointUrls.testQuickLinks, {});

            // Don't send Request if no QuickLinks should be tested.
            if (urlsToTest.length === 0) {
                resolve(quickLinks);

                return;
            }

            const source = axios.CancelToken.source();
            this.cancelTokens.push(source);

            return axios.post(testRoute, {
                'quick-links': urlsToTest
            }, {
                cancelToken: source.token
            })
                .then(result => {

                    for (const url of Object.keys(result.data)) {
                        const data = result.data[url];
                        urlToQuickLinkMap.get(url).found = (data.available ? 1 : 0);
                        urlToQuickLinkMap.get(url).showSolution = data.hasSolution;
                        urlToQuickLinkMap.get(url).external = data.external;
                        urlToQuickLinkMap.get(url).preview = data.preview;

                        if (!urlToQuickLinkMap.get(url).found) {
                            urlToQuickLinkMap.get(url).zIndex = 2;
                        }
                    }

                    resolve(quickLinks);
                })
                .catch(err => {
                    /* eslint-disable */
                    console.error(err);
                });
        });
    }

    // eslint-disable-next-line complexity
    getCombinedLinkAndTmpRedirect(link: string): { combinedLink: string, tmpRedirect: boolean } {
        // TODO: reduce cyclomatic complexity
        let combinedLink;
        let tmpRedirect = false;

        const embedLink = environment.endpointUrls.assignmentLink + '/content/embed.php?url=';
        const code = QuickLinkUtils.getLinkCode(link);
        const bookAccessCode = this.applicationStateService.userData !== undefined &&
            this.applicationStateService.userData.bookAccessCode !== undefined;
        const gmtCode = this.applicationStateService.userData !== undefined &&
            this.applicationStateService.userData.gmtCode !== undefined;

        let gmtLinkData = '';
        if (bookAccessCode) {
            gmtLinkData += `&token=${this.applicationStateService.userData.bookAccessCode.hash}`;
            gmtLinkData += `&code=${this.applicationStateService.userData.bookAccessCode.quickLink}`;
            gmtLinkData += `&plus=${this.accessService.isPlus(this.applicationStateService.userData.bookAccessCode.version) ? 1 : 0}`;
        }

        if (gmtCode) {
            gmtLinkData += `&old_token=${this.applicationStateService.userData.gmtCode.hash}`;
            gmtLinkData += `&old_code=${this.applicationStateService.userData.gmtCode.quickLink}`;
        }

        if (this.applicationStateService.userData.isTeacher) {
            gmtLinkData += `&teacher=1`;
        } else {
            gmtLinkData += `&teacher=0`;
        }

        if (this.applicationStateService.userData.digi4SchoolUser) {
            gmtLinkData += '&d4s=1';
        } else {
            gmtLinkData += '&d4s=0';
        }

        // TODO: Refactor with the Usage of QuickLinkUtils
        if (code.match(QuickLinkUtils.getIntroRegex())) {
            combinedLink = 'help_' + code;
            tmpRedirect = true;

        } else if (code.match(QuickLinkUtils.getGMTRegex(this.applicationStateService.bookConfig.quickLinkPrefix))) {
            combinedLink = `https://www.mathe-trainer.com/genial/?link=${code}`;
            combinedLink += gmtLinkData;

            tmpRedirect = true;
        } else if (code.match(QuickLinkUtils.getESquirrelRegex()) || code.match(/bm-k([01])?([0-9])$/gi)) {
            // second regex was added to match eSquirrel links with different style in bm books
            combinedLink = `${this.restService.getBaseUrl()}/${code}`;
            tmpRedirect = true;

        } else if (code === 'TT1rechnen' || code === 'TT2rechnen') {
            combinedLink = 'https://play.google.com/store/apps/details?id=com.BildungsverlagLemberger.Rechnen';
            tmpRedirect = true;

        } else if (code === 'ABC1ar') {
            combinedLink = 'https://play.google.com/store/apps/details?id=com.abc1.ar';
            tmpRedirect = true;

        } else if (code === 'ABC2lesen' || code === 'ABC1lesen') {
            combinedLink = 'https://play.google.com/store/apps/details?id=com.VereinOffenesLernen.Lesen';
            tmpRedirect = true;

        } else if (code === 'ABC2schreiben' || code === 'ABC1schreiben') {
            combinedLink = 'https://play.google.com/store/apps/details?id=com.bd421.schreiben';
            tmpRedirect = true;

        } else if (code.match(/ABC2schreiben(.*)/gi) || code.match(/ABC1schreiben(.*)/gi)) {
            combinedLink = 'https://play.google.com/store/apps/details?id=com.bd421.schreiben';
            tmpRedirect = true;

        } else if (code.match(/l[1-4]{1}s(.*)+/gi)) {
            // Fix for incorrect printed quick links of "Life" books
            combinedLink = embedLink + link;
            tmpRedirect = false;

        } else if (code.match(/(.*)s([0-9]+)s([0-9]+)$/gi)) {
            // Fix for incorrect quick links with "S" inside (f.e. GD2)
            combinedLink = embedLink + link;
            tmpRedirect = false;

        } else if (code.match(/(.*)s([0-9]+)$/gi)) {
            // "site quick links" should not be activated (Must be last position)
            return undefined;

        } else if (QuickLinkUtils.isHSMLinkPattern(code)) {
            return undefined;
        } else {
            combinedLink = embedLink + link;
        }

        return { combinedLink, tmpRedirect };
        /* tslint:enable */
    }

    /**
     * Cancels all pending requests.
     */
    cancelRequests(): void {
        this.cancelTokens.forEach(ele => {
            if (ele) {
                ele.cancel();
            }
        });

        this.cancelTokens = [];
    }

    /**
     * Get search quick link results for a given search text / query.
     * Store search results (to show globally) if requested
     *
     * @param {string} query
     * @param prefix
     * @param {boolean} store
     * @returns {Promise<Array<SearchResult>>}
     */
    findQuickLinks(query: string, prefix?: string, store = true): Promise<Array<QuickLinkSearchResult>> {
        const data = { query } as any;
        if (prefix) {
            data.prefix = prefix;
        }

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinkExtendSearch, {});

        return this.restService.get(route, data);
    }

    /**
     * Get search quick link results for a given search text / query and cancels the previous request.
     * Store search results (to show globally) if requested
     *
     * @param {string} query
     * @param prefix
     * @param {boolean} store
     * @returns {Promise<Array<QuickLinkSearchResult>>}
     */
    findQuickLinksCancelable(query: string, prefix: string, store = true): AxiosPromise {
        if (this.cancelToken) {
            this.cancelToken.cancel();
        }

        this.cancelToken = axios.CancelToken.source();

        const data = { query } as any;
        if (prefix) {
            data.prefix = prefix;
        }

        const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.quickLinkExtendSearch, {});

        return axios.get(route, {
            withCredentials: true,
            cancelToken: this.cancelToken.token,
            params: data
        });
    }

    /**
     * Get search quick link results for a given search text / query and cancels the previous request.
     * Store search results (to show globally) if requested
     *
     * @param {string} query
     * @param id
     * @returns {Promise<Array<QuickLinkSearchResult>>}
     */
    findHSMLinksCancelable(query: string, id: number): AxiosPromise {
        if (this.cancelHSMToken) {
            this.cancelHSMToken.cancel();
        }

        this.cancelHSMToken = axios.CancelToken.source();

        const data = { query } as any;
        if (id) {
            data.id = id;
        }

        const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.hsmLinksSearch, {});

        return axios.get(route, {
            withCredentials: true,
            cancelToken: this.cancelHSMToken.token,
            params: data
        });
    }

    /**
     * Checks if the given position is big enough to be on the real page (not inside the picture of an other page)
     * returns false if the link is too small
     *
     * @param {Position} position
     * @returns {boolean}
     */
    isBigEnough(position: Position): boolean {
        return position.w * position.h > 5 && position.w > 0.3 && position.h > 0.99;
    }

    /**
     * convert quick links to (exercise) name (cut link)
     *
     * @param {string} link
     * @param {boolean} addPrefixIfNotExist
     * @returns {string}
     */
    convertQuickLink(link: string, addPrefixIfNotExist?: boolean): string {
        let value = link.toLowerCase();

        for (const filter of environment.quickLinkFilter) {
            // Trim the domain from  environment.quickLinkFilter
            if (value.includes(filter.toLowerCase())) {
                value = value.substring(value.indexOf(filter.toLowerCase()) + filter.length);
            }
        }

        const prefix = this.applicationStateService.bookConfig.quickLinkPrefix;
        if (!prefix || !addPrefixIfNotExist) {
            return value;
        }

        // Add quicklink prefix if not exists and not "special" project like Geniale Projekte (GP) etc.
        if (!value.includes(prefix.toLowerCase()) &&
            !prefix.toLowerCase().startsWith('w') &&
            !['gp', 'bm', 'bd', 'be', 'hsm/'].some(prefix => value.startsWith(prefix))) {
            value = prefix + value.toLowerCase();
        }
        if (value.startsWith('hsm/')) {
            value = value.replace('hsm/', '');
        }

        // Resubmission special case. Book prefixes starting with "w", targets jsons with w prefix.
        // Texts in the book are printed without "w". So we have to change the quicklink target.
        if (prefix.toLowerCase().startsWith('w') && ('w' + value).toLowerCase().startsWith(prefix.toLowerCase())) {
            value = 'w' + value;
        }

        return value;

    }

    /**
     * Checks if the link is from a domain of the platform.
     * @param link
     */
    isPlatformDomain(link: string): boolean {

        // New sub domain m.digi.schule (HAM) was introduced. We have to match the hostname.
        link = link.replace('http://', '')
            .replace('https://', '')
        ;

        for (const domain of environment.platformDomains) {
            if (link.startsWith(domain)) {
                return true;
            }

        }

        return false;
    }

    /**
     * Tells if a QuickLink is signable.
     * @param quickLink
     */
    isSignableQuicklink(quickLink: QuickLink): boolean {

        const eSquirrelCondition = this.isSignableESquirrelQuickLink(quickLink);
        const eduvidualCondition = this.isSignableEduvidualQuickLink(quickLink);
        const siaCondition = this.isSignableSiaQuickLink(quickLink);
        const yedoCondition = this.isSignableYedoQuickLink(quickLink);
        const phaseCondition = this.isSignablePhaseQuickLink(quickLink);
        const ltiConnectionCondition = this.isSignableLtiConnection(quickLink);

        return ltiConnectionCondition ||eSquirrelCondition || eduvidualCondition || siaCondition || yedoCondition || phaseCondition;
    }

    /**
     * Tells if the QuickLink is a signable phase-6 QuickLink.
     * @param quickLink
     */
    isSignablePhaseQuickLink(quickLink: QuickLink): boolean {
        return quickLink.isSignable && quickLink.isPhase();
    }

    /**
     * Tells if the QuickLink is a signable eSquirrel QuickLink.
     * @param quickLink
     */
    isSignableESquirrelQuickLink(quickLink: QuickLink): boolean {
        return quickLink.isSignable &&
            (
                (this.applicationStateService.bookConfig.fullESquirrelFeature && this.userDataService.isDigi4SchoolBook()) ||
                (this.applicationStateService.bookConfig.fullESquirrelFeatureBac && this.userDataService.hasBookAccessCode()) ||
                this.userDataService.isPlusFeatureAccessible()
            );
    }

    /**
     * Tells if the QuickLink is a signable Eduvidual QuickLink.
     * @param quickLink
     */
    isSignableEduvidualQuickLink(quickLink: QuickLink): boolean {
        return quickLink.isOther() && this.isEduvidualTarget(quickLink) && this.isSignableLtiConnection(quickLink);
    }

    /**
     * Check if target is eduvidual link
     * @param quickLink
     */
    isEduvidualTarget(quickLink: QuickLink): boolean {
        return quickLink.redirect && quickLink.redirect.includes('https://www.eduvidual.at/');
    }

    /**
     * Tells if the QuickLink is a signable SIA QuickLink.
     * @param quickLink
     */
    isSignableSiaQuickLink(quickLink: QuickLink): boolean {
        return quickLink.isOther() && quickLink.isSignable &&
            this.applicationStateService.bookConfig.bookSource === 'sia';
    }

    /**
     * Tells if the QuickLink is a signable yedo QuickLink.
     * @param quickLink
     */
    isSignableYedoQuickLink(quickLink: QuickLink): boolean {
        return quickLink.isSignable && quickLink.isYedo();
    }

    /**
     * Determine if external link is approved.
     *
     * @param quickLink
     */
    isApprovedLink(quickLink: QuickLink): boolean {
        if (!this.applicationStateService.bookConfig.esquirrelVerified && quickLink.isESquirrel()) {
            return false;
        }

        return !(!this.applicationStateService.bookConfig.gmtVerified && quickLink.isGMT());
    }

    /**
     * @description Some quicklinks / features are allowed to open without a code.
     *              Soft access defines additional requirements like access codes.
     *
     * @example Without an access code for example esquirrel is redirected to the chapter
     * @example Digi/Other(Assignments) are opened in the external hsm.
     *
     * @param type
     */
    isAccessibleWithoutSoftAccess(type: QuickLinkType | string = 'type'): boolean {
        switch (type) {
            case QuickLinkType.OTHER: // Single assignments always (TODO: eduvidual should not be type of OTHER)
            case QuickLinkType.E_SQUIRREL: // Demo available
            case QuickLinkType.YEDO: // Demo available
                return this.applicationStateService.bookConfig.bookPermissions.quickLinks.view;
            case QuickLinkType.HSM:
            case QuickLinkType.DIGI: // ??
                return false;
            // return this.applicationStateService.bookConfig.bookPermissions.hsmLinks.view;
            default:
                return false;
        }
    }

    /**
     * Open quick link
     * @param quicklink
     */
    openQuicklink(quicklink: QuickLink): boolean {
        if (this.applicationStateService.isOfflineModeEnabled) {
            this.openQuicklinkInOfflineMode(quicklink);

            return;
        }

        if (quicklink.external > 0) {
            this.linkService.checkDigi4SchoolRedirect(quicklink.redirect || quicklink.link);

            return;
        }

        quicklink.wasClicked = true;

        if (quicklink.isSolutionLink) {
            this.updateBypassCheck(quicklink);
        }

        if (this.shouldShowFeatureDisabledModal(quicklink) && quicklink.combinedLink && quicklink.combinedLink.indexOf('help') !== 0) {
            this.modalService.openModal(quicklink);

            return;
        }

        if (quicklink.isDigi() || quicklink.isDigiFit()) {
            if (quicklink.isDigi()) {
                this.logUsage(quicklink, QuickLinkType.DIGI, TechnicalFeature.Digi);
            }

            if (quicklink.isDigiFit()) {
                this.logUsage(quicklink, QuickLinkType.DIGI_FIT, TechnicalFeature.DigiFit);
            }

            if (!this.applicationStateService.bookConfig.bookPermissions.exerciseSeries.view) {
                this.modalService.openDisabledDigiTrainerModal();

                return;
            }

            this.modalService.openModal(DigiTrainerModalComponent);

            return;
        }

        if (quicklink.isESquirrel()) {
            this.logUsage(quicklink, QuickLinkType.E_SQUIRREL, TechnicalFeature.Esquirrel);
            this.handleESquirrelQuickLink(quicklink);

            return;
        }

        if (quicklink.isCalcuflow()) {
            if (quicklink.custom_worksheet_id) {
                this.logUsage(quicklink, QuickLinkType.CALCUFLOW, TechnicalFeature.Worksheet);
            } else {
                this.logUsage(quicklink, QuickLinkType.CALCUFLOW, TechnicalFeature.Calcuflow);
            }
            this.handleCalcuflowQuickLink(quicklink);

            return;
        }

        if (quicklink.isPhase()) {
            this.logUsage(quicklink, QuickLinkType.PHASE, TechnicalFeature.Phase);
            this.handlePhaseQuickLink(quicklink);

            return;
        }

        if (quicklink.isYedo()) {
            this.logUsage(quicklink, QuickLinkType.YEDO, TechnicalFeature.Yedo);
            this.handleYedoQuickLink(quicklink);

            return;
        }

        if (quicklink.isEduSuite()) {
            this.logUsage(quicklink, QuickLinkType.EDU_SUITE, TechnicalFeature.EduSuite);
            this.handleEduSuiteQuickLink(quicklink);

            return;
        }

        if (quicklink.isDigbi()) {
            this.logUsage(quicklink, QuickLinkType.DIGBI, TechnicalFeature.Digbi);
            this.handleDigbiQuickLink(quicklink);

            return;
        }

        if (quicklink.isMathion()) {
            this.logUsage(quicklink, QuickLinkType.MATHION, TechnicalFeature.Mathion);
        }
        if (quicklink.isMathe4Alle()) {
            this.logUsage(quicklink, QuickLinkType.MATHE4ALLE, TechnicalFeature.Mathe4Alle);
        }
        if (quicklink.isMatheGym()) {
            this.logUsage(quicklink, QuickLinkType.MATHE_GYM, TechnicalFeature.MatheGym);
        }

        if (this.isExternalToolLink(quicklink) && !quicklink.isSignable) {
            this.openRedirectQuickLink(quicklink);

            return;
        }

        if (quicklink.isOther()) {
            if (this.isEduvidualTarget(quicklink)) {
                this.logUsage(quicklink, QuickLinkType.OTHER, TechnicalFeature.Eduvidual);
            } else {
                this.logUsage(quicklink, QuickLinkType.OTHER, TechnicalFeature.Other);
            }
        }

        if ((quicklink.isOther() || quicklink.isSIA()) && quicklink.isSignable) {

            if (quicklink.isSIA()) {
                quicklink.id = QuickLinkType.SIA;
                this.logUsage(quicklink, QuickLinkType.SIA, TechnicalFeature.Sia);
            }
            this.handleDistributorQuickLink(quicklink);

            return;
        }

        if (quicklink.isOther() && quicklink.redirect && !this.isPlatformDomain(quicklink.redirect)) {
            this.openRedirectQuickLink(quicklink);

            return;
        } else if (quicklink.isOther() && quicklink.redirect) {
            window.open(quicklink.redirect, '_blank');

            return;
        }

        if (quicklink.isOther() &&
            quicklink.link !== 'hsmSidebar' &&
            !this.applicationStateService.bookConfig.hsmQuicklinks
        ) {
            this.openQuickLinkAsLink(quicklink);

            return;
        }

        if (quicklink.isHSM()) {
            this.logUsage(quicklink, QuickLinkType.HSM, TechnicalFeature.AssignmentUsage);
        }

        if (quicklink.isGMT()) {
            this.modalService.openModal(quicklink);
            this.logUsage(quicklink, QuickLinkType.GMT, TechnicalFeature.Gmt);
            return;
        }

        if (quicklink.tmpRedirect) {
            if (quicklink.combinedLink && quicklink.combinedLink.indexOf('help_') === 0) {
                this.helpService.setExternalHelpLink(this.routeService.getAbsoluteRoute('front', [])
                    + quicklink.combinedLink.split('_')[1]);
                this.helpService.openHelp();

                return;
            }

            this.linkService.checkDigi4SchoolRedirect(quicklink.combinedLink);

            return;
        }

        this.modalService.openModal(quicklink);
    }

    /**
     * Converts the QuickLink to a Link and open the QuickLink in the LinkModal.
     */
    openQuickLinkAsLink(quicklink: QuickLink): void {
        const link = this.linkFactory.buildFromQuickLink(quicklink, Position.createEmptyPosition());
        link.wasClicked = true;
        this.modalService.openModal(link);
    }

    /**
     * Determine if a quicklink is accessible
     * @param quickLink
     */
    public canBypassCheck(quickLink: QuickLink): boolean {
        switch (true) {
            case quickLink.isESquirrel():
                return (this.userDataService.hasBookAccessCode() &&
                        this.applicationStateService.bookConfig.bookPermissions.eSquirrelLinks.view) ||
                    this.applicationStateService.bookConfig.fullESquirrelFeatureBac;
            case quickLink.isPhase():
                return (this.userDataService.hasBookAccessCode() &&
                    this.applicationStateService.bookConfig.bookPermissions.phaseLinks.view);
            case quickLink.isHSM():
                return (this.userDataService.hasBookAccessCode() && this.applicationStateService.bookConfig.bookPermissions.hsmLinks.view);
            case quickLink.isYedo():
                return this.applicationStateService.bookConfig.bookPermissions.siaLinks.view;
            case quickLink.isGMT():
                if (this.applicationStateService.bookConfig.maxSchoolLevel <= 8 ||
                    this.applicationStateService.bookConfig.maxSchoolLevel === null) {
                    return true;
                }

                return (this.userDataService.hasBookAccessCode() && this.applicationStateService.bookConfig.bookPermissions.gmtLinks.view);
            case quickLink.isSIA():
                return (this.userDataService.hasBookAccessCode() && this.applicationStateService.bookConfig.bookPermissions.siaLinks.view);
            case quickLink.isOther():
                return (this.applicationStateService.bookConfig.bookPermissions.quickLinks.view &&
                    this.userDataService.hasBookAccessCode());
            case quickLink.isDigbi():
                return this.applicationStateService.bookConfig.bookPermissions.digbi.view;
            case quickLink.isMathion():
                return this.applicationStateService.bookConfig.bookPermissions.mathion.view;
            case quickLink.isMathe4Alle():
                return this.applicationStateService.bookConfig.bookPermissions.mathe4Alle.view;
            case quickLink.isMatheGym():
                return this.applicationStateService.bookConfig.bookPermissions.matheGym.view;
            default:
                return false;
        }
    }

    /**
     * Log use of a quicklink
     *
     * @param quicklink
     * @param id
     * @param logType
     */
    logUsage(quicklink: QuickLink, id: QuickLinkType, logType: TechnicalFeature): Promise<boolean> {
        quicklink.logType = logType;
        quicklink.id = id;
        quicklink.tool = null;
        return this.crudService.log(quicklink) as Promise<boolean>;
    }

    private handleResult(factory: QuickLinkFactory, resultArray: Array<any>, type?: QuickLinkType) {
        resultArray.forEach(ele => {
            const quickLink = factory.buildFromObject(ele);
            this.setCombinedLinkAndTmpRedirect(quickLink);
            quickLink.bypassCheck = this.canBypassCheck(quickLink);
            if (type) {
                quickLink.type = type;
            }

            // DigiFit Quicklinks are special menus and should be above every other element.
            if (type === QuickLinkType.DIGI_FIT) {
                quickLink.zIndex = 31;
            }

            this.quickLinks.addToMap(quickLink, this.getQuickLinksByType(type));
        });

        this.applicationStateService.bookConfig.quickLinksAvailability[type] = this.getQuickLinksByType(type).size > 0;
    }

    private getQuickLinksByType(type?: QuickLinkType): Map<number, Array<QuickLink>> {
        switch (type) {
            case QuickLinkType.E_SQUIRREL:
                return this.quickLinks.eSquirrelQuickLinks;
            case QuickLinkType.GMT:
                return this.quickLinks.gmtQuickLinks;
            case QuickLinkType.HSM:
                return this.quickLinks.hsmQuickLinks;
            case QuickLinkType.DIGI_FIT:
                return this.quickLinks.digiFitQuicklinks;
            default:
                return this.quickLinks.genericQuickLinks;
        }
    }

    private async getDigiTrainerContentFromQuicklink(quicklink: string): Promise<DigiTrainerContent> {
        const url = this.routeService.getRelativeRoute(environment.endpointUrls.quicklink, { quicklink });

        let result;
        const source = axios.CancelToken.source();
        this.cancelTokens.push(source);

        try {
            result = await axios.get(url, {
                cancelToken: source.token
            });
        } catch (error) {
            result = null;
        }

        return this.collectDigiTrainerContent(result?.data);
    }

    private async collectDigiTrainerContent(result: Assignment): Promise<DigiTrainerContent> {
        const source: Assignment | any = (result?.feature?.training) ? result.feature : result;
        const digiTrainerContent: DigiTrainerContent = Object.assign(new DigiTrainerContent(), source);

        // exercises is the new value. Fallback to "passend"
        if (source?.training?.exercises === undefined && source?.training?.passend) {
            digiTrainerContent.training.exercises = source.training.passend;
        }

        if (this.digiTrainerIndividualContent === null) {
            await this.fetchDigiTrainerIndividualContent();
        }
        if (this.digiTrainerIndividualContent) {
            digiTrainerContent.training.individual = this.digiTrainerIndividualContent;
        }

        const empty =
            !digiTrainerContent.training?.competence?.length &&
            !digiTrainerContent.training?.exercises?.length &&
            !digiTrainerContent.training?.individual?.length;

        return empty ? null : digiTrainerContent;
    }

    private async fetchDigiTrainerIndividualContent(): Promise<void> {
        if (this.applicationStateService.userData.bookAccessCode) {
            const route = this.routeService.getRelativeRoute(environment.endpointUrls.portfolioIndividual,
                { code: this.applicationStateService.userData.bookAccessCode.quickLink });

            try {
                const response = await this.restService.get(route);
                if (response && response.training && response.training[this.applicationStateService.bookConfig?.quickLinkPrefix.toUpperCase()]) {
                    this.digiTrainerIndividualContent = response.training[this.applicationStateService.bookConfig?.quickLinkPrefix.toUpperCase()];
                } else {
                    this.digiTrainerIndividualContent = false;
                }
            } catch (error) {
                this.digiTrainerIndividualContent = false;
                console.info(error);
            }
        }
    }

    private setCombinedLinkAndTmpRedirect(quickLink: QuickLink): void {
        const combinedLinkAndTmpRedirect = this.getCombinedLinkAndTmpRedirect(quickLink.link);
        if (!combinedLinkAndTmpRedirect) {
            quickLink.found = 0;

            return;
        }

        quickLink.combinedLink = combinedLinkAndTmpRedirect.combinedLink;
        quickLink.tmpRedirect = combinedLinkAndTmpRedirect.tmpRedirect;
    }

    private shouldShowFeatureDisabledModal(quicklink: QuickLink): boolean {
        return (!this.featureActivationService.quickLinksActivated ||
            !quicklink.bypassCheck) && !QuickLinkUtils.getIntroRegex().test(quicklink.link);
    }

    /**
     * Updates the bypassCheck with the bypassCheck value of generic QuickLinks.
     * This is necessary to prevent opening the FeatureDisabledModal when generic QuickLinks were already clicked.
     */
    private updateBypassCheck(quicklink: QuickLink): void {
        const genericQuickLink = this.quickLinks?.genericQuickLinks?.values()?.next()?.value?.[0];
        if (genericQuickLink) {
            quicklink.bypassCheck = genericQuickLink.bypassCheck;
        }
    }

    /**
     * Open an external redirect link. Special case here is eduvidual.
     * Normal links should open with a d4s check.
     * @param quicklink
     * @private
     */
    private openRedirectQuickLink(quicklink: QuickLink): void {
        if (this.isSignableEduvidualQuickLink(quicklink) ||
            !this.applicationStateService.bookConfig.bookPermissions.quickLinks.view
        ) {
            this.modalService.openModal(quicklink);

            return;
        }

        if (!quicklink.wasClicked) {
            const link = this.linkFactory.buildFromQuickLink(quicklink, Position.createEmptyPosition());
            link.url = quicklink.redirect;
            link.wasClicked = true;
            this.modalService.openModal(link);

            return;
        }

        this.linkService.checkDigi4SchoolRedirect(quicklink.redirect);
    }

    /**
     * Handles eSquirrel QuickLink logic.
     */
    private handleESquirrelQuickLink(quicklink: QuickLink): void {
        // No bypass. Feature disabled will open
        if (!quicklink.bypassCheck) {
            this.modalService.openModal(quicklink);

            return;
        }

        // No LTI possible. open as link
        if (!this.userDataService.hasBookAccessCode() || !this.canBypassCheck(quicklink)) {
            if (!this.applicationStateService.bookConfig.esquirrelVerified) {
                const link = new Link();
                link.url = quicklink.combinedLink;
                link.bypassCheck = true;
                this.modalService.openModal(link);

                return;
            }

            window.window.open(quicklink.combinedLink);

            return;
        }

        this.modalService.openModal(quicklink);
    }

    /**
     * Handles Phase 6 QuickLink logic.
     */
    private handlePhaseQuickLink(quicklink: QuickLink): void {
        this.modalService.openModal(quicklink);
    }

    /**
     * Handle calcuflow quicklink.
     *
     * @param quicklink
     * @private
     */
    private handleCalcuflowQuickLink(quicklink: QuickLink): void {
        this.modalService.openModal(quicklink);
    }

    /**
     * Handles Distributor QuickLink logic.
     */
    private handleDistributorQuickLink(quicklink: QuickLink): void {
        this.modalService.openModal(quicklink);
    }

    /**
     * Handle yedo lti login or redirect
     */
    private handleYedoQuickLink(quicklink: QuickLink): void {
        // LTI login not possible. just open the chapter for demo access
        if (quicklink.bypassCheck &&
            (
                quicklink.getStatus() === ResponseStatus.NOT_ACCESSIBLE ||
                quicklink.getStatus() === ResponseStatus.ERROR ||
                !this.canBypassCheck(quicklink)
            )
        ) {
            window.window.open(quicklink.redirect);

            return;
        }

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quicklinkSignYedo, {
            bookId: this.applicationStateService.bookId
        });

        const $modal = new ModalableYedo();
        $modal.bypass = true;
        $modal.status = ResponseStatus.ACCESSIBLE;
        this.modalService.openModal($modal);

        this.restService.post(route, { target: quicklink.redirect ?? quicklink.link }).then(result => {
            $modal.formData = result as QuickLinkSignFormData;
        }).catch(error => {
            if (error.status === 401 || error.status === 403) {
                quicklink.status = ResponseStatus.NOT_ACCESSIBLE;
                this.modalService.closeModal($modal.getModalId());
                $modal.bypass = false;
                this.modalService.openModal($modal);
            } else {
                quicklink.status = ResponseStatus.ERROR;
            }
        });
    }

    /**
     *
     * @param quicklink
     * @private
     */
    private openQuicklinkInOfflineMode(quicklink: QuickLink): void {
        if (!navigator.onLine) {
            this.modalService.openNoInternetModal();

            return;
        }

        let link = this.applicationStateService.baseUrl + '/' + quicklink.slug;

        window.open(link, '_blank');
    }

    public isSignableLtiConnection(quickLink: QuickLink): boolean {
        let signable = false;
        _.each(this.applicationStateService.bookConfig.ltiConnections, (ltiConnection: string) => {
            const regex = new RegExp(ltiConnection);
            if (regex.test(quickLink.link)) {
                signable = true;
            }
        });

        return signable;
    }

    /**
     *
     * @param quicklink
     * @private
     */
    private isExternalToolLink(quicklink: QuickLink): boolean {
        return quicklink.type === QuickLinkType.MATHE4ALLE || quicklink.type === QuickLinkType.MATHE_GYM ||
            quicklink.type === QuickLinkType.EDUVIDUAL || quicklink.type === QuickLinkType.MATHION;
    }

    private handleDigbiQuickLink(quicklink: QuickLink): void {
        const $modal = new ModalableDigbi();
        $modal.bypass = true;
        $modal.status = ResponseStatus.ACCESSIBLE;

        $modal.contentUrl = quicklink.link;
        $modal.isTeacher = this.applicationStateService.userData?.isTeacher === true;
        $modal.storeUrl = 'https://store.at';
        if (this.applicationStateService.userData?.isTeacher) {
            $modal.storeUrl = quicklink.redirect;
        }

        this.modalService.openModal($modal);
    }

    private handleEduSuiteQuickLink(quicklink: QuickLink) {
        // LTI login not possible. just open the chapter for demo access
        if (quicklink.bypassCheck &&
            (
                quicklink.getStatus() === ResponseStatus.NOT_ACCESSIBLE ||
                quicklink.getStatus() === ResponseStatus.ERROR
            )
        ) {
            this.modalService.openDisabledEduSuiteModal(quicklink.getStatus());

            return;
        }

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinkSignEduSuite, {
            bookId: this.applicationStateService.bookId
        });

        const $modal = new ModalableEdusuite();
        $modal.bypass = true;
        $modal.status = ResponseStatus.ACCESSIBLE;
        this.modalService.openModal($modal);

        this.restService.post(route, { target: quicklink.redirect ?? quicklink.link }).then(result => {
            $modal.formData = result as QuickLinkSignFormData;
        }).catch((error) => {
            if (error.status === 401 || error.status === 403) {
                quicklink.status = ResponseStatus.NOT_ACCESSIBLE;
                $modal.status = quicklink.getStatus();
            } else {
                quicklink.status = ResponseStatus.ERROR;
                $modal.status = quicklink.getStatus();
            }

            $modal.storeUrl = error.storeUrl;
            $modal.trialUrl = error.trialUrl;

            this.modalService.closeModal($modal.getModalId());
            this.modalService.openModal($modal);
        });
    }
}
